<template>
  <div class="quote-sections-panel">
    <!-- Panel Header (only show if not embedded) -->
    <div v-if="!embedded" class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="tune" color="primary" class="q-mr-sm" />
            Quote Sections
          </div>
          <div class="panel-subtitle">
            Organize quote items into logical sections for your tender document
          </div>
        </div>
        <div class="col-auto">
          <!-- Unallocated Items Indicator -->
          <div v-if="totalUnallocatedItems !== null" class="q-mr-md">
            <q-badge 
              :color="totalUnallocatedItems > 0 ? 'warning' : 'positive'"
              :outline="totalUnallocatedItems === 0"
              class="q-pa-sm text-bold"
            >
              <template v-if="totalUnallocatedItems > 0">
                <q-icon name="warning" class="q-mr-xs" />
                {{ totalUnallocatedItems }} unallocated item{{ totalUnallocatedItems > 1 ? 's' : '' }}
              </template>
              <template v-else>
                <q-icon name="check_circle" class="q-mr-xs" />
                All items allocated
              </template>
            </q-badge>
          </div>
          <q-btn
            color="primary"
            icon="add"
            label="Add Section"
            class="add-btn"
            @click="openAddSectionDialog"
          />
        </div>
      </div>
    </div>
    
    <!-- Embedded Controls (show when embedded) -->
    <div v-else class="embedded-controls q-mb-md">
      <div class="row items-center justify-between">
        <div class="col-auto">
          <!-- Unallocated Items Indicator -->
          <div v-if="totalUnallocatedItems !== null">
            <q-badge 
              :color="totalUnallocatedItems > 0 ? 'warning' : 'positive'"
              :outline="totalUnallocatedItems === 0"
              class="q-pa-sm text-bold"
            >
              <template v-if="totalUnallocatedItems > 0">
                <q-icon name="warning" class="q-mr-xs" />
                {{ totalUnallocatedItems }} unallocated item{{ totalUnallocatedItems > 1 ? 's' : '' }}
              </template>
              <template v-else>
                <q-icon name="check_circle" class="q-mr-xs" />
                All items allocated
              </template>
            </q-badge>
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            color="primary"
            icon="add"
            label="Add Section"
            class="add-btn"
            size="sm"
            @click="openAddSectionDialog"
          />
        </div>
      </div>
    </div>

    <!-- Debug Info -->
    <div v-if="error" class="debug-section q-pa-md">
      <q-banner color="negative" class="q-mb-md">
        <q-icon name="error" class="q-mr-sm" />
        Error Loading Sections: {{ error }}
        <template #action>
          <q-btn flat dense size="sm" color="white" label="Debug Info" @click="showDebugInfo = true" />
        </template>
      </q-banner>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="flex flex-center q-pa-xl">
        <q-spinner color="primary" size="3em" />
        <div class="q-ml-md text-h6 text-grey-7">Loading sections...</div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state q-pa-lg text-center">
      <q-icon name="error" size="4em" color="negative" />
      <div class="text-h6 q-mt-md">Failed to load sections</div>
      <q-btn color="primary" label="Retry" class="q-mt-md" @click="loadSections" />
    </div>

    <!-- Empty State -->
    <div v-else-if="!sections.length" class="empty-state">
      <q-icon name="tune" size="4em" color="grey-5" />
      <div class="empty-title">No Sections Created</div>
      <div class="empty-subtitle">
        Create sections to organize quote items for presentation in the tender
      </div>
      <q-btn
        color="primary"
        icon="add"
        label="Create First Section"
        class="q-mt-md"
        @click="openAddSectionDialog"
      />
    </div>

    <!-- Sections List -->
    <div v-else class="sections-content">
      <!-- Table Header -->
      <div class="sections-header-row">
        <div class="header-cell order-col">Order</div>
        <div class="header-cell name-col">Section Name</div>
        <div class="header-cell desc-col">Description</div>
        <div class="header-cell items-col">Items</div>
        <div class="header-cell total-col">Total</div>
        <div class="header-cell visible-col">Visible</div>
        <div class="header-cell actions-col">Actions</div>
      </div>

      <!-- Section Items with Drag-and-Drop -->
      <draggable
        v-model="sections"
        item-key="id"
        handle=".drag-handle"
        ghost-class="drag-ghost"
        drag-class="dragging"
        class="sections-grid"
        @end="updateSectionOrders"
      >
        <template #item="{ element: section }">
          <div
            class="section-row clickable-row"
            :class="{ 'section-hidden': !section.is_visible }"
            @click="manageItems(section)"
          >
            <div class="section-cell order-col" @click.stop>
              <div class="flex items-center justify-center">
                <q-icon name="drag_indicator" class="cursor-move drag-handle q-mr-xs" />
                <span class="order-number">{{ section.display_order }}</span>
              </div>
            </div>

            <div class="section-cell name-col">
              <div class="section-name">{{ section.name }}</div>
            </div>

            <div class="section-cell desc-col">
              <div class="section-description">
                {{ section.description || 'No description' }}
              </div>
              <q-tooltip v-if="section.description && section.description.length > 50">
                {{ section.description }}
              </q-tooltip>
            </div>

            <div class="section-cell items-col text-center">
              <q-badge
                :color="section.quoteItems?.length > 0 ? 'primary' : 'grey'"
                :label="section.quoteItems?.length || 0"
              />
            </div>

            <div class="section-cell total-col text-center">
              <div class="section-total">{{ formatCurrency(section.subtotal || 0) }}</div>
            </div>

            <div class="section-cell visible-col text-center" @click.stop>
              <q-toggle
                :model-value="section.is_visible"
                :color="section.is_visible ? 'positive' : 'grey'"
                :icon="section.is_visible ? 'visibility' : 'visibility_off'"
                @update:model-value="toggleSectionVisibility(section)"
              />
            </div>

            <div class="section-cell actions-col" @click.stop>
              <q-btn-group flat>
                <q-btn
                  flat
                  round
                  size="sm"
                  icon="edit"
                  color="primary"
                  @click="editSection(section)"
                >
                  <q-tooltip>Edit Section</q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  size="sm"
                  icon="layers"
                  color="accent"
                  @click="manageItems(section)"
                >
                  <q-tooltip>Manage Items</q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  size="sm"
                  icon="delete"
                  color="negative"
                  @click="confirmDeleteSection(section)"
                >
                  <q-tooltip>Delete Section</q-tooltip>
                </q-btn>
              </q-btn-group>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- Add/Edit Section Dialog -->
    <SectionDialog
      v-model="sectionDialog"
      v-model:section="currentSection"
      :edit-mode="editMode"
      :saving="saving"
      @save="saveSection"
      @field-changed="handleFieldChange"
    />

    <!-- Manage Section Items Dialog -->
    <q-dialog v-model="itemsDialog" maximized persistent>
      <q-card class="manage-items-dialog">
        <!-- Enhanced Header -->
        <div class="dialog-header bg-primary text-white">
          <div class="row items-center">
            <div class="col">
              <div class="dialog-title">
                <q-icon name="layers" class="q-mr-sm" />
                Manage Items: {{ currentSection.section_name }}
              </div>
              <div class="dialog-subtitle">
                Organize quote items within this section
              </div>
            </div>
            <div class="col-auto">
              <q-btn 
                v-close-popup 
                flat 
                round 
                icon="close" 
                color="white"
                size="lg"
              />
            </div>
          </div>
        </div>

        <!-- Content Area -->
        <div class="dialog-content">
          <div class="row q-col-gutter-lg full-height">
            <!-- Available Items Panel -->
            <div class="col-12 col-md-6">
              <ItemSelectionPanel
                v-model:item-search="itemSearch"
                v-model:item-type-filter="itemTypeFilter"
                :items="availableItems"
                :loading="loading"
                @add-item="addItemToSection"
              />
            </div>

            <!-- Section Items Panel -->
            <div class="col-12 col-md-6">
              <div class="items-panel">
                <SectionItemsPanel
                  v-model:items="sectionItems"
                  :section="currentSection"
                  :loading="false"
                  :readonly="false"
                  :show-reorder="true"
                  :show-debug-info="showDebugInfo"
                  @remove-item="removeItemFromSection"
                  @reorder="updateItemOrders"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Enhanced Footer -->
        <div class="dialog-footer">
          <div class="row items-center justify-between">
            <div class="col-auto">
              <div class="footer-info">
                <span class="info-text">{{ sectionItems.length }} item{{ sectionItems.length !== 1 ? 's' : '' }} in section</span>
              </div>
            </div>
            <div class="col-auto">
              <q-btn-group>
                <q-btn 
                  v-close-popup 
                  flat 
                  label="Cancel" 
                  color="grey-8" 
                />
                <q-btn 
                  unelevated 
                  label="Save Changes" 
                  color="primary" 
                  icon="save"
                  :loading="savingItems" 
                  @click="saveItemChanges" 
                />
              </q-btn-group>
            </div>
          </div>
        </div>
      </q-card>
    </q-dialog>

    <!-- Confirm Delete Dialog -->
    <q-dialog v-model="confirmDeleteDialog">
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm">Are you sure you want to delete this section?</span>
        </q-card-section>

        <q-card-section>
          <p class="q-ma-none">This will remove the section <strong>{{ currentSection.section_name }}</strong> and unassign all items in it.</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" color="primary" />
          <q-btn flat label="Delete" color="negative" :loading="deleting" @click="deleteSection" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Debug Info Dialog -->
    <q-dialog v-model="showDebugInfo">
      <q-card style="min-width: 700px">
        <q-card-section>
          <div class="text-h6">Debug Information</div>
        </q-card-section>

        <q-card-section>
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
              <q-list bordered>
                <q-item>
                  <q-item-section>
                    <q-item-label caption>Tender ID</q-item-label>
                    <q-item-label>{{ tenderId }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label caption>Quote ID (from URL)</q-item-label>
                    <q-item-label>{{ quoteId }}</q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label caption>Using API Path</q-item-label>
                    <q-item-label>
                      <q-badge color="positive">
                        /api/tenders/{{tenderId}}/quotes/{{quoteId}}/sections
                      </q-badge>
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label caption>API Response Status</q-item-label>
                    <q-item-label>
                      <q-badge :color="debugData.status === 200 ? 'positive' : 'negative'">
                        {{ debugData.status }} {{ debugData.statusText }}
                      </q-badge>
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-item>
                  <q-item-section>
                    <q-item-label caption>Available Quote Items</q-item-label>
                    <q-item-label>{{ availableItems.length }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>

            <div class="col-12 col-md-6">
              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle2">Error Details</div>
                  <div v-if="error" class="text-negative">{{ error }}</div>
                  <div v-else class="text-positive">No errors</div>
                </q-card-section>
              </q-card>

              <div class="q-mt-md">
                <q-btn
                  unelevated
                  color="primary"
                  label="Reload Data"
                  class="full-width"
                  @click="loadSections" />
              </div>

              <div class="q-mt-sm">
                <q-btn
                  outline
                  color="secondary"
                  label="Open Debug Tool"
                  class="full-width"
                  @click="openDebugTool" />
              </div>
            </div>
          </div>

          <q-expansion-item label="Response Data" header-class="text-primary" class="q-mt-md">
            <q-card>
              <q-card-section>
                <pre class="debug-json">{{ JSON.stringify(debugData, null, 2) }}</pre>
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <q-expansion-item label="API Endpoints" header-class="text-primary" class="q-mt-md">
            <q-card>
              <q-card-section>
                <p><strong>API Lookup Process:</strong></p>
                <ol>
                  <li>First fetch tender details: <code>/api/tenders/{{ tenderId }}</code></li>
                  <li>Find tender_quote with matching quote_id = {{ quoteId }}</li>
                  <li>Use tender_quote.id as the tenderQuoteId</li>
                  <li>Then fetch sections using: <code>/api/tenders/{{ tenderId }}/quotes/{{ quoteId }}/sections</code></li>
                </ol>
                <div class="q-mt-md">
                  <p><strong>Current Values:</strong></p>
                  <p><strong>Quote ID:</strong> <code>{{ quoteId }}</code></p>
                  <p><strong>Status:</strong> <q-badge color="positive">Direct quote ID used</q-badge></p>
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Close" color="primary" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
  import { useQuoteStore } from '../../stores/quotes';
  import { tenderQuoteSectionService } from '../../services/tenderQuoteSectionService';
  import { api } from '../../services/api';
  import draggable from 'vuedraggable';
  import ItemCard from './ItemCard.vue';
  import ItemSelectionPanel from './ItemSelectionPanel.vue';
  import SectionItemsPanel from './SectionItemsPanel.vue';
  import SectionDialog from './SectionDialog.vue';

  export default {
    name: 'TenderQuoteSections',
    components: {
      draggable,
      ItemCard,
      ItemSelectionPanel,
      SectionItemsPanel,
      SectionDialog,
    },
    props: {
      tenderId: {
        type: [Number, String],
        required: true,
      },
      quoteId: {
        type: [Number, String],
        required: true,
      },
      embedded: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        loading: false,
        error: null,
        sections: [],
        sectionDialog: false,
        editMode: false,
        currentSection: {
          section_name: '',
          section_description: '',
          display_order: 1,
          is_visible: true,
        },
        saving: false,
        confirmDeleteDialog: false,
        deleting: false,
        showDebugInfo: false,
        debugData: {},
        // actualTenderQuoteId: deprecated - now we use quoteId directly
        useDirectProxy: false, // Flag to use direct proxy mode for all API calls
      
        // Unallocated items tracking
        totalQuoteItems: [],
        totalAllocatedItemIds: [],

        // Items dialog data
        itemsDialog: false,
        sectionItems: [],
        availableItems: [],
        itemSearch: '',
        itemTypeFilter: 'all',
        savingItems: false,
      };
    },

    computed: {
      quoteStore() {
        return useQuoteStore();
      },

      // Total unallocated items across all sections
      totalUnallocatedItems() {
        if (!this.totalQuoteItems.length) {
          return null; // Not loaded yet
        }
      
        // Create a map of all labor items by their IDs (both formats)
        const laborItems = this.totalQuoteItems
          .filter((item) => item.fromLabor || item.isLabor || item.type === 'labor');
      
        // Create a map of all item IDs, including special handling for labor items
        const allItemsByType = {
          labor: [],
          regular: [],
        };
      
        // Separate labor and regular items
        this.totalQuoteItems.forEach((item) => {
          // Check if this is a labor item
          const isLabor = item.fromLabor || item.isLabor || item.type === 'labor' || 
            (typeof item.id === 'string' && item.id.startsWith('labor_'));
                       
          if (isLabor) {
            let laborId = null;
          
            // Try to normalize the labor ID
            if (item.labor_id) {
              laborId = item.labor_id.toString();
            } else if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
              laborId = item.id.replace('labor_', '');
            } else {
              laborId = item.id.toString();
            }
          
            allItemsByType.labor.push({
              id: item.id.toString(),
              normalizedId: laborId,
              item: item,
            });
          } else {
            // Regular items just use their ID
            allItemsByType.regular.push({
              id: item.id.toString(),
              item: item,
            });
          }
        });
      
        // Get all allocated item IDs
        const allocatedIds = this.totalAllocatedItemIds
          .filter((id) => id && typeof id !== 'undefined')
          .map((id) => id.toString());
      
        // Find unallocated items by type
        const unallocatedByType = {
          labor: [],
          regular: [],
        };
      
        // Check each labor item
        allItemsByType.labor.forEach((laborItem) => {
          // Check if either the full ID or normalized ID is in allocated IDs
          const isAllocated = allocatedIds.includes(laborItem.id) || 
            allocatedIds.includes(laborItem.normalizedId) ||
            allocatedIds.includes(`labor_${laborItem.normalizedId}`);
                           
          if (!isAllocated) {
            unallocatedByType.labor.push(laborItem);
          }
        });
      
        // Check regular items
        allItemsByType.regular.forEach((regularItem) => {
          // Convert to string and trim any whitespace
          const normalizedId = String(regularItem.id).trim();
          // Also check for numeric equality (to handle number vs string)
          const numericId = parseInt(normalizedId, 10);
        
          const isAllocated = allocatedIds.includes(normalizedId) || 
            allocatedIds.includes(numericId.toString()) ||
            allocatedIds.some((id) => parseInt(id, 10) === numericId);
                          
          if (!isAllocated) {
            unallocatedByType.regular.push(regularItem);
          }
        });
      
        // Calculate totals
        const totalUnallocated = unallocatedByType.labor.length + unallocatedByType.regular.length;

        return totalUnallocated;
      },
    },

    created() {
      // No longer need to look up tender_quote_id - we use quote_id directly
      this.loadSections();
    },

    methods: {
      // Get proper display name for an item, handling special cases
      getDisplayName(item) {
        // Handle special group IDs that might come from the backend
        if (typeof item.id === 'string' && item.id.startsWith('labor_group_')) {
          const laborType = item.id.replace('labor_group_', '');
          return `Labor: ${laborType}`;
        }

        if (typeof item.id === 'string' && item.id.startsWith('supplier_quote_')) {
          const quoteId = item.id.replace('supplier_quote_', '');
          return item.display_description || item.description || `Supplier Quote #${quoteId}`;
        }

        // Parse metadata if it's a string
        let metadata = item.metadata;
        if (metadata && typeof metadata === 'string') {
          try {
            metadata = JSON.parse(metadata);
          } catch (e) {
            // Ignore parse errors
            metadata = null;
          }
        }

        // Check if this is a subcontractor quote using the new metadata fields
        // Priority 1: Check metadata.isSubcontractorQuote flag
        if (metadata?.isSubcontractorQuote === true) {
          return `Subcontractor Quote: ${item.description || item.name || 'Quote #' + item.id}`;
        }

        // Priority 2: Check metadata.laborType for subcontractor_quote
        if (metadata?.laborType === 'subcontractor_quote') {
          return `Subcontractor Quote: ${item.description || item.name || 'Quote #' + item.id}`;
        }

        // Priority 3: Check combination of document_id and type=labor (legacy detection)
        if (item.document_id && item.type === 'labor' && !metadata?.laborType) {
          // This might be a subcontractor quote if no other laborType is specified
          return `Subcontractor Quote: ${item.description || item.name || 'Quote #' + item.id}`;
        }

        // Priority 4: Legacy fields
        if (item.type === 'subcontractor_quote' || item.fromSubcontractorQuote) {
          if (item.display_description) {
            return item.display_description;
          }
          return `Subcontractor Quote: ${item.description || 'Quote #' + item.id}`;
        }

        // For regular labor items (not subcontractor quotes), provide better names
        if (item.is_labor || item.item_type === 'labor' || item.type === 'labor' || item.fromLabor) {
          // Use metadata.laborType if available
          if (metadata?.laborType && metadata.laborType !== 'subcontractor_quote') {
            return `${metadata.laborType} Labor: ${item.description || 'Labor'}`;
          }

          // Try metadata fields (check both camelCase and snake_case for compatibility)
          if (metadata && (metadata.laborType || metadata.original_type || metadata.original_labor_type)) {
            const laborType = metadata.laborType || metadata.original_type || metadata.original_labor_type;
            return `${laborType} Labor: ${item.description || 'Labor'}`;
          }

          // Fall back to description or a generic labor name
          return item.display_description || item.description || `Labor Item #${item.id}`;
        }

        // Use display_description first, then description, then fallback
        return item.display_description || item.description || `Item #${item.id}`;
      },

      // Transform items into logical groupings for tender presentation
      createLogicalItemGroupings(rawItems) {
        const groupedItems = [];
      
        // Group supplier items by document_id (multiple parts from same supplier quote)
        const supplierQuotesByDoc = {};
        const individualSupplierQuotes = [];
        const subcontractorQuotes = [];
        const laborItems = [];
        const regularItems = [];
      
        // Track processed items to prevent duplicates
        const processedItems = new Set();
      
        // Categorize all items
        rawItems.forEach((item) => {
          const itemKey = `${item.id}_${item.type || 'unknown'}`;

          // Skip if already processed
          if (processedItems.has(itemKey)) {
            return;
          }
          processedItems.add(itemKey);

          // Parse metadata if it's a string
          let metadata = item.metadata;
          if (metadata && typeof metadata === 'string') {
            try {
              metadata = JSON.parse(metadata);
            } catch (e) {
              // Ignore parse errors
              metadata = null;
            }
          }

          // Check for labor items first (most specific)
          const isLabor = item.type === 'labor' || item.fromLabor || item.isLabor ||
            (typeof item.id === 'string' && item.id.startsWith('labor_'));

          // Check if this is a subcontractor quote using metadata
          const isSubcontractorQuote = metadata?.isSubcontractorQuote === true ||
            metadata?.laborType === 'subcontractor_quote' ||
            item.type === 'subcontractor_quote' ||
            item.fromSubcontractorQuote;

          if (isSubcontractorQuote) {
            // Subcontractor quotes (regardless of other fields)
            subcontractorQuotes.push(item);
          } else if (isLabor && !item.document_id) {
            // Regular labor items (not from subcontractor quotes)
            laborItems.push(item);
          } else if (item.type === 'supplier_part' && item.document_id) {
            // Group supplier parts by document_id
            if (!supplierQuotesByDoc[item.document_id]) {
              supplierQuotesByDoc[item.document_id] = [];
            }
            supplierQuotesByDoc[item.document_id].push(item);
          } else if (item.type === 'supplier_quote' || (item.fromSupplierQuote && !item.document_id)) {
            // Individual supplier quotes
            individualSupplierQuotes.push(item);
          } else if (isLabor && item.document_id && !isSubcontractorQuote) {
            // Labor items with document_id that aren't subcontractor quotes
            // These might be labor items from supplier quotes
            laborItems.push(item);
          } else if (!isLabor && item.type !== 'supplier_quote' && item.type !== 'supplier_part' &&
            item.type !== 'subcontractor_quote') {
            // Regular product items (catch-all for non-labor, non-supplier items)
            regularItems.push(item);
          }
        });
      
        // 1. Add grouped Supplier Quotes (parts from same document)
        Object.entries(supplierQuotesByDoc).forEach(([documentId, parts]) => {
          if (parts.length > 0) {
            // Calculate total for all parts in this supplier quote
            const totalValue = parts.reduce((sum, part) => {
              const quantity = Number(part.quantity) || 0;
              const unitPrice = Number(part.unit_price) || 0;
              const markup = Number(part.markup) || 0;
              return sum + (quantity * unitPrice * (1 + markup / 100));
            }, 0);
          
            // Get quote description from document or first part
            const document = parts[0].document;
            const quoteDescription = document ? document.description : 'Supplier Quote';
          
            groupedItems.push({
              id: `supplier_quote_${documentId}`,
              type: 'supplier_quote_group',
              display_type: 'Supplier Quote',
              description: `Supplier Quote: ${quoteDescription} (${parts.length} items)`,
              display_description: `Supplier Quote: ${quoteDescription} (${parts.length} items)`,
              quantity: 1,
              unit_price: totalValue,
              markup: 0,
              document_id: documentId,
              grouped_items: parts,
              is_group: true,
            });
          }
        });
      
        // 2. Add individual Supplier Quotes
        individualSupplierQuotes.forEach((quote) => {
          groupedItems.push({
            ...quote,
            display_type: 'Supplier Quote',
            description: quote.description || quote.part_number || `Supplier Quote #${quote.id}`,
            display_description: `Supplier Quote: ${quote.description || quote.part_number || 'Quote #' + quote.id}`,
          });
        });
      
        // 3. Add Regular Items individually  
        regularItems.forEach((item) => {
          groupedItems.push({
            ...item,
            display_type: 'Product/Item',
            description: item.description || item.part_number || `Item #${item.id}`,
            display_description: item.description || item.part_number || `Item #${item.id}`,
          });
        });
      
        // 4. Add Subcontractor Quotes as single line items
        subcontractorQuotes.forEach((quote) => {
          groupedItems.push({
            ...quote,
            display_type: 'Subcontractor Quote',
            description: quote.description || `Subcontractor Quote #${quote.id}`,
            display_description: `Subcontractor Quote: ${quote.description || 'Quote #' + quote.id}`,
          });
        });
      
        // 5. Group Labor Items by type
        const laborByType = {};
        laborItems.forEach((item) => {
          // Get the original labor type from metadata
          let laborType = 'Installation'; // default
          if (item.metadata) {
            let metadata = item.metadata;
            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
              } catch (e) {
                console.error('Failed to parse tender item metadata:', e);
              }
            }
            if (metadata && (metadata.laborType || metadata.original_type || metadata.original_labor_type)) {
              laborType = metadata.laborType || metadata.original_type || metadata.original_labor_type;
            }
          }
        
          if (!laborByType[laborType]) {
            laborByType[laborType] = [];
          }
          laborByType[laborType].push(item);
        });
      
        // Add grouped labor items
        Object.entries(laborByType).forEach(([type, items]) => {
          if (items.length === 1) {
            // Single labor item - add as is
            groupedItems.push({
              ...items[0],
              display_type: `Labor: ${type}`,
              description: items[0].description || `${type} Labor`,
              display_description: items[0].description || `${type} Labor`,
            });
          } else {
            // Multiple labor items of same type - group them
            const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
            const avgUnitPrice = items.reduce((sum, item) => sum + (item.unit_price || 0), 0) / items.length;
            const avgMarkup = items.reduce((sum, item) => sum + (item.markup || 0), 0) / items.length;
          
            groupedItems.push({
              id: `labor_group_${type}`,
              type: 'labor_group',
              display_type: `Labor: ${type}`,
              description: `${type} Labor (${items.length} items)`,
              display_description: `${type} Labor (${items.length} items)`,
              quantity: totalQuantity,
              unit_price: avgUnitPrice,
              markup: avgMarkup,
              grouped_items: items,
              is_group: true,
            });
          }
        });
      
        return groupedItems;
      },

      // DEPRECATED: No longer needed since we use quote_id directly
      // Kept for backward compatibility
      async lookupTenderQuoteId() {
        await this.loadSections();
        return;

      // OLD CODE - kept for reference
      // The tender_quotes junction table no longer exists
      // Quotes now have a direct tender_id foreign key
      /*
      try {

        // There's a critical distinction we need to address first:
        // The URL needs the tender_quote_id (from the join table), NOT the quote_id
        // The frontend often confuses these two concepts

        // Directly query for the tender_quote record that maps tender to quote
        const tenderDetailUrl = `/api/tenders/${this.tenderId}`;
        const tenderResponse = await fetch(tenderDetailUrl, {
          headers: {
            'Authorization': api.getHeaders()['Authorization']
          }
        });

        if (!tenderResponse.ok) {
          throw new Error(`Failed to fetch tender details: ${tenderResponse.status} ${tenderResponse.statusText}`);
        }

        const tenderData = await tenderResponse.json();

        let tenderQuoteId = null;

        // Look for quotes in the tender data
        if (tenderData.success && tenderData.data && tenderData.data.quotes) {
          // Standard success format with data wrapper
          const quotes = tenderData.data.quotes;
          const tenderQuote = quotes.find(q => q.quote_id === parseInt(this.quoteId));
          if (tenderQuote) {
            tenderQuoteId = tenderQuote.id;
          }
        } else if (tenderData.quotes) {
          // Alternate format without data wrapper
          const quotes = tenderData.quotes;
          const tenderQuote = quotes.find(q => q.quote_id === parseInt(this.quoteId));
          if (tenderQuote) {
            tenderQuoteId = tenderQuote.id;
          }
        }
        
        if (tenderQuoteId) {
          this.actualTenderQuoteId = tenderQuoteId;
          await this.loadSections();
        } else {
          // If we couldn't find it through the API, try a workaround with a test script
          
          try {
            const testUrl = `/test-section-url.php?tender_id=${this.tenderId}&quote_id=${this.quoteId}`;
            const testResponse = await fetch(testUrl);
            const testHtml = await testResponse.text();
            
            // Extract the tender quote ID from HTML response using regex
            const match = testHtml.match(/Found tender-quote association: ID = (\d+)/i);
            if (match && match[1]) {
              tenderQuoteId = parseInt(match[1]);
              this.actualTenderQuoteId = tenderQuoteId;
              await this.loadSections();
            } else {
              throw new Error(`No tender-quote association found between tender ${this.tenderId} and quote ${this.quoteId}`);
            }
          } catch (testErr) {
            throw new Error(`No tender quote association found for quote ${this.quoteId} in tender ${this.tenderId}`);
          }
        }
      } catch (err) {
        this.error = err.message || `Unable to load sections. Please try a different quote.`;
        this.loading = false;
        
        // Show an error notification with retry option
        this.$q.notify({
          color: 'negative',
          message: `Error: ${this.error}`,
          icon: 'error',
          timeout: 0,
          actions: [{
            label: 'Retry',
            color: 'white',
            handler: () => {
              this.lookupTenderQuoteId();
            }
          }]
        });
      }
      */
      },

      async loadSections() {
        this.loading = true;
        this.error = null;
        this.debugData = {};

        try {
          // Use quote_id directly - no longer need the actualTenderQuoteId lookup
          if (!this.quoteId) {
            throw new Error('No quote ID provided.');
          }

          // Use the standardized API service to fetch sections
          // Now we pass quote_id instead of tender_quote_id
          const data = await tenderQuoteSectionService.getSections(
            this.tenderId,
            this.quoteId,
          );
        
          // Save for debugging
          this.debugData = {
            status: 200,
            statusText: 'OK (via standardized API service)',
            data,
          };

          // Handle the new standardized API response format
          // The api.js service will unwrap the data layer, so we need to handle that
          if (Array.isArray(data)) {
            // If data is already an array, use it directly
            this.sections = data;
          } else if (data.sections && Array.isArray(data.sections)) {
            // If data contains a sections array property, use that
            this.sections = data.sections;
          } else {
            // If no sections array is found, try to determine what we received
          
            // As a fallback, if data looks like a single section, make it an array
            if (data.id && data.section_name) {
              this.sections = [data];
            } else {
              // Last resort: empty array
              this.sections = [];
            }
          }
        
          // Process section data to ensure it properly includes items
          this.sections.forEach((section) => {
            // Ensure subtotal is a number (convert string to number if needed)
            if (section.subtotal !== undefined && section.subtotal !== null) {
              section.subtotal = parseFloat(section.subtotal);
            } else if (section.total_price !== undefined && section.total_price !== null) {
              // Fallback to total_price if subtotal is missing
              section.subtotal = parseFloat(section.total_price);
            } else {
              // If no subtotal provided, calculate it from items
              section.subtotal = 0;
            }

            // First check if we have the all_items property (combined items)
            if (section.all_items && Array.isArray(section.all_items)) {
              // Use all_items as the primary source of data
              section.allItems = [...section.all_items];

              // Separate into quoteItems and laborItems for backward compatibility
              section.quoteItems = section.all_items.filter((item) => !item.is_labor && item.item_type !== 'labor');
              section.laborItems = section.all_items.filter((item) => item.is_labor || item.item_type === 'labor');
            } else {
              // Fall back to separate quoteItems and laborItems properties
            
              // First try to find regular items in any of the possible property names
              if (section.quote_items && Array.isArray(section.quote_items)) {
                // Snake case version (quote_items)
                section.quoteItems = [...section.quote_items];
              } else if (section.items && Array.isArray(section.items)) {
                // Some API responses use 'items' instead of 'quoteItems'
                section.quoteItems = [...section.items];
              } else if (!section.quoteItems) {
                // Try to find items in any array property
                let foundItems = false;
                for (const key in section) {
                  const value = section[key];
                  if (key !== 'quoteItems' && Array.isArray(value) && value.length > 0 && value[0].id) {
                    section.quoteItems = [...value];
                    foundItems = true;
                    break;
                  }
                }

                if (!foundItems) {
                  // Initialize as empty array if missing
                  section.quoteItems = [];
                }
              }
            
              // Initialize laborItems property if missing
              if (!section.laborItems) {
                // Initialize as empty array if missing
                section.laborItems = [];
              }
            
              // Create combined allItems array
              section.allItems = [...section.quoteItems, ...section.laborItems];
            }

            // If subtotal is still 0, calculate it from items
            if (section.subtotal === 0 && section.allItems && section.allItems.length > 0) {
              const calculatedSubtotal = section.allItems.reduce((sum, item) => {
                const quantity = item.quantity || 1;
                const unitPrice = item.unit_price || 0;
                const markup = item.markup || 0;
                const itemTotal = quantity * unitPrice * (1 + markup / 100);
                return sum + itemTotal;
              }, 0);

              if (calculatedSubtotal > 0) {
                section.subtotal = calculatedSubtotal;
              }
            }
          });
        
          // Sort by display order
          this.sections.sort((a, b) => a.display_order - b.display_order);
        
          // Calculate total allocated items
          // Reset allocated items tracking
          this.totalAllocatedItemIds = [];
        
          // Collect all allocated item IDs from all sections
          this.sections.forEach((section) => {
            // Use the allItems array which contains both regular and labor items
            if (section.allItems && section.allItems.length) {
              section.allItems.forEach((item) => {
                // Handle regular item
                if (item.id) {
                  // Always convert ID to string for consistent comparison
                  const normalizedId = String(item.id).trim();
                  if (!this.totalAllocatedItemIds.includes(normalizedId)) {
                    this.totalAllocatedItemIds.push(normalizedId);
                  }
                }
              
                // Handle parts group - add all individual part IDs
                if (item.id === 'parts_group' && item.is_group && Array.isArray(item.item_ids)) {
                  item.item_ids.forEach((partId) => {
                    if (partId) {
                      const normalizedPartId = String(partId).trim();
                      if (!this.totalAllocatedItemIds.includes(normalizedPartId)) {
                        this.totalAllocatedItemIds.push(normalizedPartId);
                      }
                    }
                  });
                }
              });
            } else {
              // Fallback to separate quoteItems and laborItems arrays
              // Process regular items
              if (section.quoteItems && section.quoteItems.length) {
                section.quoteItems.forEach((item) => {
                  // Handle regular item
                  if (item.id) {
                    // Always convert ID to string for consistent comparison
                    const normalizedId = String(item.id).trim();
                    if (!this.totalAllocatedItemIds.includes(normalizedId)) {
                      this.totalAllocatedItemIds.push(normalizedId);
                    }
                  }
                
                  // Handle parts group - add all individual part IDs
                  if (item.id === 'parts_group' && item.is_group && Array.isArray(item.item_ids)) {
                    item.item_ids.forEach((partId) => {
                      if (partId) {
                        const normalizedPartId = String(partId).trim();
                        if (!this.totalAllocatedItemIds.includes(normalizedPartId)) {
                          this.totalAllocatedItemIds.push(normalizedPartId);
                        }
                      }
                    });
                  }
                });
              }
            
              // Process labor items
              if (section.laborItems && section.laborItems.length) {
                section.laborItems.forEach((item) => {
                  if (item.id) {
                    const normalizedId = String(item.id).trim();
                    if (!this.totalAllocatedItemIds.includes(normalizedId)) {
                      this.totalAllocatedItemIds.push(normalizedId);
                    }
                  }
                });
              }
            }
          });
        
          // Load all quote items if we don't have them yet
          if (!this.totalQuoteItems.length) {
            this.loadAllQuoteItems();
          }
        } catch (err) {
          this.error = err.message;
        } finally {
          this.loading = false;
        }
      },

      async loadQuoteItems() {
        try {
          // Try to fetch the quote with all details
          const quote = await this.quoteStore.fetchQuote(this.quoteId, {
            includeDetails: true,
            includeParts: true,
          });

          if (quote && quote.items) {
            // Map items to our format
            return quote.items.map((item) => ({
              ...item,
              // Add a type field if not already present
              type: item.type || (
                item.fromSupplierQuote ? 'supplier_quote' :
                item.fromLabor ? 'labor' : 'product'
              ),
            }));
          }

          return [];
        } catch (err) {
          this.error = 'Failed to load quote items: ' + err.message;
          return [];
        }
      },
    
      // Load all quote items for counting unallocated items
      async loadAllQuoteItems() {
        try {
          // Load all items (regular and labor) in a single call
          await this.loadRegularItems();
        } catch (err) {
          this.totalQuoteItems = [];
        }
      },
    
      async loadRegularItems() {
        try {
          // Fetch the quote with all items (both regular and labor)
          const quote = await this.quoteStore.fetchQuote(this.quoteId, {
            includeDetails: true,
            includeParts: true,
          });
        
          if (quote && quote.items) {
            // Process regular items to ensure all have unique IDs
            let regularItems = quote.items.filter((item) => {
              // Filter out items that are labor items
              const isLabor = item.fromLabor || item.isLabor || item.type === 'labor' || 
                (typeof item.id === 'string' && item.id.toString().startsWith('labor_'));
              return !isLabor && item.id && typeof item.id !== 'undefined';
            });
          
            // Add a type field if not already present and normalize IDs
            regularItems = regularItems.map((item) => ({
              ...item,
              // Add a type field if not already present
              type: item.type || (
                item.fromSupplierQuote ? 'supplier_quote' : 'product'
              ),
              // Ensure ID is a normalized string for consistent comparison
              id: String(item.id).trim(),
            }));
          
            // Process labor items from quote
            let laborItems = [];
            if (quote.labor_items && Array.isArray(quote.labor_items)) {
              // Transform labor items to have consistent structure
              laborItems = quote.labor_items.map((item) => ({
                ...item,
                type: item.type || 'labor',
                isLabor: true,
                fromLabor: true,
                // Add labor_ prefix to ID if not already there
                id: item.id.toString().startsWith('labor_') ? item.id : `labor_${item.id}`,
                // Also keep original ID for reference
                originalId: item.id,
              }));
            }

            // Store in totalQuoteItems
            this.totalQuoteItems = [...regularItems, ...laborItems];
          }
        } catch (err) {
          console.error('Failed to load regular items:', err);
        }
      },
    
      // Labor items are now loaded together with regular items in loadRegularItems

      // We now directly use the proper API
    
      // REMOVED: fetchLaborItemsForSections method is no longer needed 
      // since we now receive both regular and labor items in the sections API response

      openDebugTool() {
        // Open the debug tool in a new tab
        const debugUrl = `/debug-tender-sections.php?tender_id=${this.tenderId}&quote_id=${this.quoteId}`;
        window.open(debugUrl, '_blank');
      },

      openAddSectionDialog() {
        this.editMode = false;
        this.currentSection = {
          section_name: '',
          section_description: '',
          display_order: this.sections.length + 1,
          is_visible: true,
        };
        this.sectionDialog = true;
      },

      editSection(section) {
        this.editMode = true;
        this.currentSection = { ...section };
        this.sectionDialog = true;
      },

      async saveSection() {
        if (!this.currentSection.section_name) {
          return; // Form validation should handle this
        }

        this.saving = true;

        try {
          const sectionData = {
            name: this.currentSection.section_name, // Backend expects 'name'
            description: this.currentSection.section_description, // Backend expects 'description'
            display_order: this.currentSection.display_order,
            is_visible: this.currentSection.is_visible,
          };

          if (this.editMode) {
            // Update existing section using service
            await tenderQuoteSectionService.updateSection(
              this.tenderId,
              this.quoteId,
              this.currentSection.id,
              sectionData,
            );
          } else {
            // Create new section using service
            await tenderQuoteSectionService.addSection(
              this.tenderId,
              this.quoteId,
              sectionData,
            );
          }

          // Close dialog and refresh sections
          this.sectionDialog = false;

          // Force a complete refresh of all data
          await this.loadSections();
        } catch (err) {
          this.error = err.message;
        } finally {
          this.saving = false;
        }
      },

      // Auto-save individual section fields (for edit mode only)
      async saveSectionField(fieldName) {
        if (!this.editMode || !this.currentSection.id) {
          return; // Only auto-save in edit mode
        }

        try {
          const sectionData = {
            name: this.currentSection.section_name, // Backend expects 'name'
            description: this.currentSection.section_description, // Backend expects 'description'
            display_order: this.currentSection.display_order,
            is_visible: this.currentSection.is_visible,
          };

          await tenderQuoteSectionService.updateSection(
            this.tenderId,
            this.quoteId,
            this.currentSection.id,
            sectionData,
          );

          // Update the section in the local sections array
          const index = this.sections.findIndex((s) => s.id === this.currentSection.id);
          if (index !== -1) {
            this.sections[index] = { ...this.sections[index], ...this.currentSection };
          }

        // Silent success - no notification for auto-save
        } catch (err) {
          this.$q.notify({
            type: 'negative',
            message: `Failed to save ${fieldName.replace('_', ' ')}: ${err.message}`,
            position: 'top',
          });
        }
      },

      // Handle field change events from SectionDialog
      handleFieldChange({ field }) {
        this.saveSectionField(field);
      },

      confirmDeleteSection(section) {
        this.currentSection = { ...section };
        this.confirmDeleteDialog = true;
      },

      async deleteSection() {
        this.deleting = true;

        try {
          await tenderQuoteSectionService.deleteSection(
            this.tenderId,
            this.quoteId,
            this.currentSection.id,
          );

          // Close dialog and refresh sections
          this.confirmDeleteDialog = false;
          await this.loadSections();
        } catch (err) {
          this.error = err.message;
        } finally {
          this.deleting = false;
        }
      },

      async manageItems(section) {
        this.currentSection = { ...section };
        this.sectionItems = [];
        this.availableItems = [];
        this.itemSearch = '';
        this.itemTypeFilter = 'all';
        this.itemsDialog = true;

        try {
          // Use service to fetch section with all items
          const sectionData = await tenderQuoteSectionService.getSection(
            this.tenderId,
            this.quoteId,
            section.id,
          );

          // Save debug data
          this.debugData.data = sectionData;

          // Find the source of items - first try all_items array for combined items
          let allItems = [];
          if (sectionData.section && sectionData.section.all_items && Array.isArray(sectionData.section.all_items)) {
            // Use all_items which contains both regular and labor items
            allItems = sectionData.section.all_items;
          } else if (sectionData.all_items && Array.isArray(sectionData.all_items)) {
            // Check for top-level all_items array
            allItems = sectionData.all_items;
          } else if (sectionData.allocated_items && Array.isArray(sectionData.allocated_items)) {
            // Check for allocated_items (from the API response structure)
            allItems = sectionData.allocated_items;
          } else {
            // Fall back to separate handling of regular and labor items
            // Temporary array to hold section items before combining
            let tempRegularItems = [];
            let tempLaborItems = [];

            // Look for regular items in various places
            if (sectionData.section && sectionData.section.quote_items && sectionData.section.quote_items.length > 0) {
              tempRegularItems = [...sectionData.section.quote_items];
            } else if (sectionData.section && sectionData.section.quoteItems && sectionData.section.quoteItems.length > 0) {
              tempRegularItems = [...sectionData.section.quoteItems];
            } else if (sectionData.section && sectionData.section.items && sectionData.section.items.length > 0) {
              tempRegularItems = [...sectionData.section.items];
            } else if (sectionData.items && Array.isArray(sectionData.items)) {
              // Check for top-level items array
              tempRegularItems = [...sectionData.items];
            } else {
              // Desperate measure - look at all properties to find array of items
              if (sectionData.section) {
                for (const key in sectionData.section) {
                  const value = sectionData.section[key];
                  if (key !== 'laborItems' && key !== 'all_items' &&
                    Array.isArray(value) && value.length > 0 && value[0].id) {
                    tempRegularItems = [...value];
                    break;
                  }
                }
              }
            }

            // Look for labor items in the correct location from the API response
            if (sectionData.available_items && sectionData.available_items.section_labor_items && sectionData.available_items.section_labor_items.length > 0) {
              tempLaborItems = [...sectionData.available_items.section_labor_items];
            } else if (sectionData.section && sectionData.section.laborItems && sectionData.section.laborItems.length > 0) {
              tempLaborItems = [...sectionData.section.laborItems];
            }
          
            // Process items to ensure consistent format
            tempLaborItems = tempLaborItems.map((item) => ({
              ...item,
              type: 'labor',
              fromLabor: true,
              isLabor: true,
              id: item.id.toString(),
              item_type: 'labor',
            }));

            // Combine regular and labor items
            allItems = [...tempRegularItems, ...tempLaborItems];
          }

          // Process and organize section items for display
          if (allItems.length > 0) {
            // Extract regular items and labor items
            const regularItems = allItems.filter((item) =>
              !item.is_labor && item.item_type !== 'labor' && item.type !== 'labor' &&
              (typeof item.id !== 'string' || !item.id.toString().startsWith('labor_')));

            const laborItemsRaw = allItems.filter((item) =>
              item.is_labor || item.item_type === 'labor' || item.type === 'labor' ||
              (typeof item.id === 'string' && item.id.toString().startsWith('labor_')));

            // Process labor items to add proper display_description
            const laborItems = laborItemsRaw.map((item, index) => {
              // If item already has display_description, use it
              if (item.display_description) {
                return item;
              }

              // For subcontractor quotes, add the prefix
              if (item.type === 'subcontractor_quote' || item.fromSubcontractorQuote ||
                (item.is_labor && item.document_id)) {
                return {
                  ...item,
                  display_description: `Subcontractor Quote: ${item.description || 'Quote #' + item.id}`,
                };
              }

              // For regular labor items, add labor type prefix
              let laborType = 'Installation';
              if (item.metadata) {
                let metadata = item.metadata;
                if (typeof metadata === 'string') {
                  try {
                    metadata = JSON.parse(metadata);
                  } catch (e) {
                  // Ignore parse errors
                  }
                }
                if (metadata && (metadata.original_type || metadata.original_labor_type)) {
                  laborType = metadata.original_type || metadata.original_labor_type;
                }
              }

              return {
                ...item,
                display_description: `${laborType} Labor: ${item.description || 'Labor'}`,
              };
            });

            // Group regular parts into a single item
            const regularParts = regularItems.filter((item) => 
              !item.fromSupplierQuote && 
              !item.is_supplier_quote && 
              item.type !== 'supplier_quote');
            
            const supplierQuoteItemsRaw = regularItems.filter((item) =>
              item.fromSupplierQuote ||
              item.is_supplier_quote ||
              item.type === 'supplier_quote');

            // Process supplier quote items to add proper display_description
            const supplierQuoteItems = supplierQuoteItemsRaw.map((item) => {
              // If item already has display_description, use it
              if (item.display_description) {
                return item;
              }

              // Add "Supplier Quote:" prefix
              return {
                ...item,
                display_description: `Supplier Quote: ${item.description || 'Quote #' + item.id}`,
              };
            });
            
            // If we have regular parts, create a parts group
            if (regularParts.length > 0) {
            
              // Calculate total value
              const totalValue = regularParts.reduce((sum, part) => {
                return sum + (part.quantity * part.unit_price * (1 + (part.markup || 0) / 100));
              }, 0);
            
              // Get minimum display order from parts
              const minDisplayOrder = Math.min(...regularParts.map((part) => part.display_order || 1));
            
              // Create parts group
              const partsGroup = {
                id: 'parts_group',
                description: `All Parts (${regularParts.length} items)`,
                display_description: `All Parts (${regularParts.length} items)`,
                quantity: 1,
                unit_price: totalValue,
                markup: 0,
                type: 'parts',
                category: 'parts',
                is_group: true,
                item_count: regularParts.length,
                item_ids: regularParts.map((part) => part.id),
                display_order: minDisplayOrder,
              };
            
              // Combine all items: supplier quotes, parts group, and labor items
              this.sectionItems = [...supplierQuoteItems, partsGroup, ...laborItems];
            } else {
              // No regular parts to group, just combine supplier quotes and labor
              this.sectionItems = [...supplierQuoteItems, ...laborItems];
            }
          } else {
            this.sectionItems = [];
          }

          // Use the available_items from the API directly
          if (sectionData.available_items) {
            // Create a flat array of all available items
            const allAvailableItems = [];

            // Create a set of IDs from already allocated items across ALL sections
            // Normalize IDs to handle both numeric and string formats (e.g., "labor_15" and 15)
            const allocatedIds = new Set();

            // First, add all items from ALL sections (not just current section)
            this.sections.forEach((section) => {
              // Use the allItems array which contains both regular and labor items
              if (section.allItems && section.allItems.length) {
                section.allItems.forEach((item) => {
                  // Add the item ID as-is
                  allocatedIds.add(item.id);

                  // If it's a labor item with string ID like "labor_15", also add the numeric part
                  if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
                    const numericId = parseInt(item.id.replace('labor_', ''));
                    if (!isNaN(numericId)) {
                      allocatedIds.add(numericId);
                    }
                  }

                  // If it's a numeric ID, also add the labor_ version
                  if (typeof item.id === 'number') {
                    allocatedIds.add(`labor_${item.id}`);
                  }

                  // If this is a parts group, add all the individual part IDs too
                  if (item.id === 'parts_group' && item.is_group && Array.isArray(item.item_ids)) {
                    item.item_ids.forEach((partId) => {
                      allocatedIds.add(partId);
                      // Also add numeric version if string
                      if (typeof partId === 'string') {
                        const numericId = parseInt(partId);
                        if (!isNaN(numericId)) {
                          allocatedIds.add(numericId);
                        }
                      }
                    });
                  }
                });
              }
            });


            // Add supplier quotes (only if not already allocated)
            if (sectionData.available_items.supplier_quotes) {
              const availableSupplierQuotes = sectionData.available_items.supplier_quotes.filter((item) => {
                // Check if this item is already allocated
                const isAllocated = allocatedIds.has(item.id) ||
                  (typeof item.id === 'string' && item.id.startsWith('labor_') &&
                    allocatedIds.has(parseInt(item.id.replace('labor_', '')))) ||
                  (typeof item.id === 'number' && allocatedIds.has(`labor_${item.id}`));

                return !isAllocated;
              });
              allAvailableItems.push(...availableSupplierQuotes);
            }

            // Add labor items from all_items (only if not already allocated)
            if (sectionData.available_items.all_items) {
              const laborItems = sectionData.available_items.all_items.filter((item) =>
                item.is_labor || item.item_type === 'labor' || item.type === 'labor' ||
                (typeof item.id === 'string' && item.id.toString().startsWith('labor_')),
              );

              // Filter out already allocated labor items
              const availableLaborItems = laborItems.filter((item) => {
                // Check if this item is already allocated
                const isAllocated = allocatedIds.has(item.id) ||
                  (typeof item.id === 'string' && item.id.startsWith('labor_') &&
                    allocatedIds.has(parseInt(item.id.replace('labor_', '')))) ||
                  (typeof item.id === 'number' && allocatedIds.has(`labor_${item.id}`));

                return !isAllocated;
              });

              if (availableLaborItems.length > 0) {
                allAvailableItems.push(...availableLaborItems);
              }
            }

            // Also check for explicit labor items array
            if (sectionData.available_items.labor_items && sectionData.available_items.labor_items.length) {

              // Add markers to these items to ensure they're recognized as labor
              const laborItems = sectionData.available_items.labor_items.map((item) => ({
                ...item,
                fromLabor: true,
                isLabor: true,
                type: 'labor',
                item_type: 'labor',
              }));

              // Filter out already allocated items
              const availableLaborItems = laborItems.filter((item) => {
                const isAllocated = allocatedIds.has(item.id) ||
                  (typeof item.id === 'string' && item.id.startsWith('labor_') &&
                    allocatedIds.has(parseInt(item.id.replace('labor_', '')))) ||
                  (typeof item.id === 'number' && allocatedIds.has(`labor_${item.id}`));

                return !isAllocated;
              });

              if (availableLaborItems.length > 0) {
                allAvailableItems.push(...availableLaborItems);
              }
            }

            // Handle parts - create a parts group if multiple parts exist
            if (sectionData.available_items.all_regular_parts && sectionData.available_items.all_regular_parts.length > 0) {
              // Filter to only include parts that aren't already in the section
              const availableParts = sectionData.available_items.all_regular_parts.filter((part) => {
                const isAllocated = allocatedIds.has(part.id);
                return !isAllocated;
              });

              if (availableParts.length > 0) {
                // Calculate total value of all available parts
                const totalValue = availableParts.reduce((sum, part) => {
                  return sum + (part.quantity * part.unit_price * (1 + (part.markup || 0) / 100));
                }, 0);

                // Create a parts group
                const partsGroup = {
                  id: 'parts_group',
                  description: `All Parts (${availableParts.length} items)`,
                  quantity: 1,
                  unit_price: totalValue,
                  markup: 0,
                  type: 'parts',
                  category: 'parts',
                  is_group: true,
                  item_count: availableParts.length,
                  item_ids: availableParts.map((part) => part.id),
                };

                // Add the parts group instead of individual parts
                allAvailableItems.push(partsGroup);
              }
            }
            // Backward compatibility - if the parts are already grouped in the API response
            else if (sectionData.available_items.parts && sectionData.available_items.parts.length) {
              // Filter out already allocated parts
              const availableParts = sectionData.available_items.parts.filter((part) => {
                const isAllocated = allocatedIds.has(part.id);
                return !isAllocated;
              });

              if (availableParts.length > 0) {
                allAvailableItems.push(...availableParts);
              }
            }

            // Transform items into logical groupings for tender presentation
            this.availableItems = this.createLogicalItemGroupings(allAvailableItems);
          } else {
            // Fall back to the old method if the API doesn't return available_items
            const quoteItems = await this.loadQuoteItems();

            // Create a set of IDs from already allocated items across ALL sections (same logic as above)
            const allocatedIds = new Set();

            // Add all items from ALL sections (not just current section)
            this.sections.forEach((section) => {
              // Use the allItems array which contains both regular and labor items
              if (section.allItems && section.allItems.length) {
                section.allItems.forEach((item) => {
                  allocatedIds.add(item.id);
                  if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
                    const numericId = parseInt(item.id.replace('labor_', ''));
                    if (!isNaN(numericId)) {
                      allocatedIds.add(numericId);
                    }
                  }
                  if (typeof item.id === 'number') {
                    allocatedIds.add(`labor_${item.id}`);
                  }
                  // If this is a parts group, add all the individual part IDs too
                  if (item.id === 'parts_group' && item.is_group && Array.isArray(item.item_ids)) {
                    item.item_ids.forEach((partId) => {
                      allocatedIds.add(partId);
                      if (typeof partId === 'string') {
                        const numericId = parseInt(partId);
                        if (!isNaN(numericId)) {
                          allocatedIds.add(numericId);
                        }
                      }
                    });
                  }
                });
              }
            });

            const filteredItems = quoteItems.filter((item) => !allocatedIds.has(item.id));
            this.availableItems = this.createLogicalItemGroupings(filteredItems);

            // Additionally, fetch labor items to ensure they're included
            try {
              // Load regular items which now includes labor items
              await this.loadRegularItems();

              // Filter out labor items from totalQuoteItems that aren't already in the section
              const laborItems = this.totalQuoteItems.filter((item) => {
                // Check if this is a labor item
                const isLabor = item.fromLabor || item.isLabor || item.type === 'labor' ||
                  (typeof item.id === 'string' && item.id.toString().startsWith('labor_'));

                // Only include if it's labor and not already allocated
                if (isLabor) {
                  const isAllocated = allocatedIds.has(item.id) ||
                    (typeof item.id === 'string' && item.id.startsWith('labor_') &&
                      allocatedIds.has(parseInt(item.id.replace('labor_', '')))) ||
                    (typeof item.id === 'number' && allocatedIds.has(`labor_${item.id}`));
                  return !isAllocated;
                }
                return false;
              });
            
              if (laborItems.length > 0) {
                // Merge the labor items and regroup
                const allItems = [...this.availableItems, ...laborItems];
                this.availableItems = this.createLogicalItemGroupings(allItems);
              }
            } catch (err) {
              console.error('Failed to load labor items:', err);
            }
          }

        } catch (err) {
          this.error = err.message;
        }
      },

      // Add item to section
      addItemToSection(item) {
        // Handle the special parts_group case
        if (item.id === 'parts_group' && item.is_group && item.item_ids) {
          // Keep the parts group intact and add it as a single item
          // with all its child items instead of breaking them apart
          this.sectionItems.push({
            ...item,
            display_order: this.sectionItems.length + 1,
          });

          // Remove the group from available items
          this.availableItems = this.availableItems.filter((i) => i.id !== item.id);
        } else if (item.fromLabor || item.isLabor || item.type === 'labor' ||
          (item.id && typeof item.id === 'string' && item.id.toString().startsWith('labor_'))) {
          // Handle special case for labor items
          // Keep track of both the original ID and the numeric ID for server communication
          const laborItemToAdd = { ...item, display_order: this.sectionItems.length + 1 };

          // For labor items, we need to preserve both IDs for later use
          if (item.labor_id) {
            // If there's an explicit labor_id, use it
            laborItemToAdd.original_id = item.id;  // Store original ID format
            laborItemToAdd.numeric_id = parseInt(item.labor_id, 10);  // Store numeric version
          } else if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
            // If ID has labor_ prefix, extract the numeric part
            const numericId = parseInt(item.id.replace('labor_', ''), 10);
            laborItemToAdd.original_id = item.id;  // Store original ID format
            laborItemToAdd.numeric_id = numericId;  // Store numeric version for API
          }

          // Add to section items with all the metadata we've gathered
          this.sectionItems.push(laborItemToAdd);
        
          // Remove from available items
          this.availableItems = this.availableItems.filter((i) => i.id !== item.id);
        } else {
          // Normal item case - remove from available items
          this.availableItems = this.availableItems.filter((i) => i.id !== item.id);
        
          // Add to section items
          this.sectionItems.push({
            ...item,
            display_order: this.sectionItems.length + 1,
          });
        }
      },

      // Remove item from section
      removeItemFromSection(item) {
        // Handle special case for parts group
        if (item.id === 'parts_group' && item.is_group) {
        
          // Remove the parts group from section items
          this.sectionItems = this.sectionItems.filter((i) => i.id !== 'parts_group');
        
          // Create a new parts group for available items or update existing one
          const existingPartsGroup = this.availableItems.find((i) => i.id === 'parts_group' && i.is_group);
        
          if (existingPartsGroup) {
            // If there's already a parts group in available items, merge item IDs
            existingPartsGroup.item_ids = [...new Set([...existingPartsGroup.item_ids, ...item.item_ids])];
            existingPartsGroup.item_count = existingPartsGroup.item_ids.length;
            existingPartsGroup.description = `All Parts (${existingPartsGroup.item_count} items)`;
          
            // Update the total value if we have the data
            const data = this.debugData.data;
            if (data && data.available_items && data.available_items.all_regular_parts) {
              const allParts = data.available_items.all_regular_parts.filter(
                (part) => existingPartsGroup.item_ids.includes(part.id),
              );
            
              const totalValue = allParts.reduce((sum, part) => {
                return sum + (part.quantity * part.unit_price * (1 + (part.markup || 0) / 100));
              }, 0);
            
              existingPartsGroup.unit_price = totalValue;
            }
          } else {
            // Add the parts group back to available items
            this.availableItems.push(item);
          }
          return;
        }
      
        // Check if this is a labor item
        const isLaborItem = item.fromLabor || 
          item.isLabor || 
          item.type === 'labor' || 
          (typeof item.id === 'string' && item.id.startsWith('labor_'));
      
        if (isLaborItem) {
          // Get all possible IDs this labor item might have
          const possibleIds = [];

          // Add the original ID
          if (item.id) {
            possibleIds.push(item.id);
          }

          // Add the labor_id if it exists
          if (item.labor_id) {
            possibleIds.push(item.labor_id);
            possibleIds.push(`labor_${item.labor_id}`);
          }

          // Add the numeric_id if it exists (from our enhanced addItemToSection)
          if (item.numeric_id) {
            possibleIds.push(item.numeric_id);
            possibleIds.push(`labor_${item.numeric_id}`);
          }

          // If ID is labor_prefixed, add the non-prefixed version
          if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
            const numericPart = item.id.replace('labor_', '');
            possibleIds.push(numericPart);
          }

          // If ID is numeric, add the labor_prefixed version
          if (!isNaN(parseInt(item.id, 10))) {
            possibleIds.push(`labor_${item.id}`);
          }
        
          // Remove any duplicate IDs and ensure they're all strings for comparison
          const uniqueIds = [...new Set(possibleIds)].map((id) => String(id));
        
          // Remove the item from section items if any ID matches
          this.sectionItems = this.sectionItems.filter((i) => {
            // Convert this item's ID to string for comparison
            const thisItemId = String(i.id);
          
            // Check if this item's ID matches any of the possible IDs
            if (uniqueIds.includes(thisItemId)) {
              return false; // This means remove the item
            }

            // Also check labor_id, numeric_id and original_id if present
            if ((i.labor_id && uniqueIds.includes(String(i.labor_id))) ||
              (i.numeric_id && uniqueIds.includes(String(i.numeric_id))) ||
              (i.original_id && uniqueIds.includes(String(i.original_id)))) {
              return false; // Remove this item
            }
          
            return true; // Keep this item
          });
        } else {
          // For regular items, do a direct ID comparison
          this.sectionItems = this.sectionItems.filter((i) => i.id !== item.id);
        }
        // For regular parts, we need to check if we should add back to the parts group
        // instead of as individual items
        const data = this.debugData.data;
        if (data && data.available_items && data.available_items.parts && 
          data.available_items.parts.length && data.available_items.all_regular_parts) {
        
          // Check if this is a regular part (product)
          const isRegularPart = item.type === 'product' || 
            (!item.fromSupplierQuote && !item.fromLabor && 
              !item.type?.includes('supplier') && !item.type?.includes('labor'));
        
          if (isRegularPart) {
            // Check if we already have the parts group in available items
            const partsGroup = this.availableItems.find((i) => i.id === 'parts_group' && i.is_group);
          
            if (partsGroup) {
              // Just add the item ID to the group's item_ids
              if (!partsGroup.item_ids.includes(item.id)) {
                partsGroup.item_ids.push(item.id);
                partsGroup.item_count = partsGroup.item_ids.length;
                partsGroup.description = `All Parts (${partsGroup.item_count} items)`;
              
                // Update the total value
                const allParts = data.available_items.all_regular_parts.filter(
                  (part) => partsGroup.item_ids.includes(part.id),
                );
              
                const totalValue = allParts.reduce((sum, part) => {
                  return sum + (part.quantity * part.unit_price * (1 + (part.markup || 0) / 100));
                }, 0);
              
                partsGroup.unit_price = totalValue;
              }
            } else {
              // We need to create a new parts group
              const allParts = data.available_items.all_regular_parts;
              const availableParts = allParts.filter((part) => !this.sectionItems.some((i) => i.id === part.id));
            
              if (availableParts.length > 0) {
                // Calculate total value
                const totalValue = availableParts.reduce((sum, part) => {
                  return sum + (part.quantity * part.unit_price * (1 + (part.markup || 0) / 100));
                }, 0);
              
                // Create the parts group
                const newPartsGroup = {
                  id: 'parts_group',
                  description: `All Parts (${availableParts.length} items)`,
                  quantity: 1,
                  unit_price: totalValue,
                  markup: 0,
                  type: 'parts',
                  category: 'parts',
                  is_group: true,
                  item_count: availableParts.length,
                  item_ids: availableParts.map((part) => part.id),
                };
              
                this.availableItems.push(newPartsGroup);
              }
            }
          } else {
            // Not a regular part, just add back to available items
            this.availableItems.push(item);
          }
        } else {
          // Fallback - just add back to available items
          this.availableItems.push(item);
        }
      },

      // Update section display order after drag and drop
      async updateSectionOrders() {
        // Update local display_order properties based on new order
        this.sections.forEach((section, index) => {
          section.display_order = index + 1;
        });

        // Save to backend
        try {
          const updatePromises = this.sections.map((section) =>
            fetch(
              `/api/tenders/${this.tenderId}/quotes/${this.quoteId}/sections/${section.id}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': api.getHeaders()['Authorization'],
                },
                body: JSON.stringify({
                  display_order: section.display_order,
                }),
              },
            ),
          );

          await Promise.all(updatePromises);

          // Refresh the sections
          await this.loadSections();
        } catch (err) {
          this.error = 'Failed to update section order: ' + err.message;
        }
      },

      // Update item order after drag and drop
      updateItemOrders() {
        // Update local display_order properties
        this.sectionItems.forEach((item, index) => {
          item.display_order = index + 1;
        });
      },

      // Save item changes - using standard API endpoints with separate requests for regular and labor items
      async saveItemChanges() {
        this.savingItems = true;

        try {
          // Separate items by type: regular items and labor items
          const regularItems = [];
          const laborItems = [];
        
          // Go through all items and categorize them
          for (const item of this.sectionItems) {
            // Check if this is a labor item using multiple properties
            const isLabor = item.fromLabor || 
              item.isLabor || 
              item.type === 'labor' || 
              item.category === 'labor' ||
              item.item_type === 'labor' ||
              item.is_labor ||
              (typeof item.id === 'string' && item.id.toString().startsWith('labor_'));
          
            if (isLabor) {
              laborItems.push(item);
            } else {
              regularItems.push(item);
            }
          }
        
          // Extract regular item IDs
          const regularItemIds = [];
        
          // Process regular items including parts groups
          for (const item of regularItems) {
            if (item.id === 'parts_group' && item.is_group && Array.isArray(item.item_ids)) {
              // For parts groups, add all the individual part IDs
            
              for (const partId of item.item_ids) {
                const numericPartId = parseInt(partId, 10);
                if (!isNaN(numericPartId) && numericPartId > 0) {
                  regularItemIds.push(numericPartId);
                }
              }
            } else {
              // Regular items - just add the ID
              const itemId = parseInt(item.id, 10);
              if (!isNaN(itemId) && itemId > 0) {
                regularItemIds.push(itemId);
              }
            }
          }
        
          // Extract labor item IDs
          const laborItemIds = [];
        
          // Process labor items - extract numeric IDs
          for (const item of laborItems) {
            let laborId = null;
          
            // Try multiple different ways to get the ID
            if (item.labor_id) {
              // First prefer explicit labor_id
              laborId = parseInt(item.labor_id, 10);
            } else if (item.numeric_id) {
              // Next try stored numeric ID
              laborId = parseInt(item.numeric_id, 10);
            } else if (typeof item.id === 'string' && item.id.startsWith('labor_')) {
              // Extract from labor_ prefixed ID
              laborId = parseInt(item.id.replace('labor_', ''), 10);
            } else {
              // Try direct ID
              laborId = parseInt(item.id, 10);
            }
          
            // Add valid IDs to the list
            if (!isNaN(laborId) && laborId > 0) {
              laborItemIds.push(laborId);
            } else {
            }
          }
        
          // Remove duplicates from both arrays
          const uniqueRegularItemIds = [...new Set(regularItemIds)];
          const uniqueLaborItemIds = [...new Set(laborItemIds)];
        
          // Track API success state
          let saveSuccessful = true;
          const errors = [];

          // Use separate API calls for regular items and labor items
          // The backend expects different parameter formats for each
          try {
          
            // Use the combined service method to update both types of items
            const data = await tenderQuoteSectionService.updateAllSectionItems(
              this.tenderId,
              this.quoteId,
              this.currentSection.id,
              uniqueRegularItemIds,
              uniqueLaborItemIds,
            );
          
            if (data.status === 'error') {
              saveSuccessful = false;
            
              // Check for validation errors that require data refresh
              if (data.labor && data.labor.error_type === 'validation_error') {
                errors.push(data.labor.message || 'Some labor items are no longer available. Please refresh the page and try again.');

                // Force immediate refresh of labor data to prevent future stale references
                setTimeout(async () => {
                  await this.refreshLaborData();
                }, 100);
              } else if (data.errors && data.errors.length > 0) {
                errors.push(...data.errors);
              } else {
                errors.push('Unknown error saving items');
              }
            }
          } catch (err) {
            saveSuccessful = false;
            errors.push(`Items error: ${err.message}`);
          }
        
          // Check for errors and notify user
          if (!saveSuccessful) {
            // Check if this is a validation error
            const hasValidationError = errors.some((error) =>
              error.includes('no longer available') ||
              error.includes('Please refresh'),
            );

            // Show appropriate error notification
            this.$q.notify({
              color: 'negative',
              message: hasValidationError
                ? errors.find((error) => error.includes('no longer available') || error.includes('Please refresh'))
                : 'There was an issue saving some items. Please try again.',
              icon: 'error',
              timeout: hasValidationError ? 8000 : 5000,
            });
          } else {
          
            // Show success notification
            this.$q.notify({
              color: 'positive',
              message: 'Section items saved successfully',
              icon: 'check_circle',
              timeout: 3000,
            });
          
          // Dialog will be closed in the main success handling code above
          }
        
          // Always reload the sections to ensure we have the latest data
          // Slightly longer delay to ensure server processing is complete
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Force a complete data refresh - load sections and quote items in parallel
          await Promise.all([
            this.loadSections(),
            this.loadAllQuoteItems(),
          ]);

          // If successful, close the dialog
          if (saveSuccessful) {
            // Close dialog immediately so user sees refresh in the background
            this.itemsDialog = false;

          // No need for additional refresh - we already have the latest data
          }
        
        } catch (err) {
          this.error = err.message;
        
          // Show error notification
          this.$q.notify({
            color: 'negative',
            message: `Error: ${err.message}`,
            icon: 'error',
            timeout: 5000,
          });
        } finally {
          this.savingItems = false;
        }
      },

      /**
       * Force refresh of all labor-related data after validation errors
       * This helps prevent stale labor item references
       */
      async refreshLaborData() {
        try {
          // Clear any cached data in the quotes store for this specific quote
          if (this.quoteStore && this.quoteStore.clearQuoteCache) {
            this.quoteStore.clearQuoteCache(this.quoteId);
          }

          // Force reload of all quote items (including labor items)
          await this.loadAllQuoteItems();

          // Force reload of sections to get current assignments
          await this.loadSections();
        } catch (error) {
          console.error('Failed to update section assignments:', error);
        }
      },

      formatCurrency(value) {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
        }).format(value);
      },
    },
  };
</script>

<style lang="scss" scoped>
.quote-sections-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
    
    .add-btn {
      font-weight: 600;
      border-radius: 8px;
    }
  }

  .loading-state,
  .error-state,
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: #666;
    
    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      margin: 16px 0 8px;
    }
    
    .empty-subtitle {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }

  .sections-content {
    .sections-header-row {
      display: grid;
      grid-template-columns: 80px 120px 1fr 60px 100px 50px 120px;
      gap: 16px;
      padding: 12px 16px;
      background: #f8f9fa;
      border-radius: 8px 8px 0 0;
      border: 1px solid #e0e0e0;
      border-bottom: none;
      
      .header-cell {
        font-weight: 700;
        font-size: 0.85rem;
        color: #555;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;
        
        &.desc-col {
          text-align: left;
        }
      }
    }

    .sections-grid {
      border: 1px solid #e0e0e0;
      border-radius: 0 0 8px 8px;
      overflow: hidden;
      
      .section-row {
        display: grid;
        grid-template-columns: 80px 120px 1fr 60px 100px 50px 120px;
        gap: 16px;
        padding: 16px;
        background: white;
        border-bottom: 1px solid #f0f0f0;
        transition: all 0.2s ease;

        &.clickable-row {
          cursor: pointer;
        }

        &:hover {
          background: #e3f2fd;
        }

        &:last-child {
          border-bottom: none;
        }

        &.section-hidden {
          opacity: 0.6;
          background: #f5f5f5;

          &:hover {
            background: #eeeeee;
          }
        }
        
        .section-cell {
          display: flex;
          align-items: center;
          min-height: 40px;
          
          &.order-col {
            justify-content: center;
            
            .order-number {
              font-weight: 600;
              color: #333;
            }
          }
          
          &.name-col {
            .section-name {
              font-weight: 600;
              color: #333;
              font-size: 0.95rem;
            }
          }
          
          &.desc-col {
            .section-description {
              font-size: 0.9rem;
              color: #666;
              line-height: 1.4;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          }
          
          &.total-col {
            .section-total {
              font-weight: 600;
              color: #1976d2;
              font-size: 0.95rem;
            }
          }
          
          &.actions-col {
            justify-content: center;
          }
        }
      }
    }
  }

  .debug-section {
    .q-banner {
      border-radius: 8px;
    }
  }
}

// Drag and drop styles
.drag-ghost {
  opacity: 0.5;
  transform: rotate(5deg);
}

.dragging {
  background-color: rgba(25, 118, 210, 0.1);
  border: 2px dashed #1976d2;
  
  .section-cell {
    color: #1976d2;
  }
}

.drag-handle {
  cursor: move;
  color: #999;
  transition: color 0.2s ease;
  
  &:hover {
    color: #1976d2;
  }
}

// Responsive design
@media (max-width: 1024px) {
  .quote-sections-panel .sections-content {
    .sections-header-row,
    .sections-grid .section-row {
      grid-template-columns: 60px 100px 1fr 50px 80px 40px 100px;
      gap: 8px;
      
      .header-cell,
      .section-cell {
        font-size: 0.8rem;
      }
    }
  }
}

.debug-json {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
}

// Manage Items Dialog Styles
.manage-items-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;

  .dialog-header {
    padding: 16px 24px;
    flex-shrink: 0;

    .dialog-title {
      font-size: 1.25rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }

    .dialog-subtitle {
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }

  .dialog-content {
    flex: 1;
    padding: 16px;
    background: #f8f9fa;
    overflow: hidden;
    min-height: 0;

    .row.full-height {
      height: 100%;
    }

    .col-12.col-md-6 {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .items-panel {
      background: white;
      border-radius: 8px;
      border: 1px solid #e0e0e0;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .panel-header {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;

        .panel-title {
          font-size: 1rem;
          font-weight: 700;
          color: #333;
          display: flex;
          align-items: center;
          margin-bottom: 2px;
        }

        .panel-subtitle {
          color: #666;
          font-size: 0.8rem;
        }
      }

      .panel-controls {
        padding: 12px 16px;
        background: #f8f9fa;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
      }

      .items-list {
        flex: 1;
        overflow-y: auto;
        min-height: 0;

        .empty-state {
          text-align: center;
          padding: 32px 16px;
          color: #666;

          .empty-title {
            font-size: 0.95rem;
            font-weight: 600;
            margin: 12px 0 6px;
          }

          .empty-subtitle {
            font-size: 0.85rem;
            line-height: 1.4;
          }
        }

        .items-grid {
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .debug-info {
          padding: 12px;
          background: #f5f5f5;
          border-radius: 8px;
          margin: 12px;

          .debug-title {
            font-weight: 600;
            margin-bottom: 6px;
          }
        }
      }
    }
  }

  .dialog-footer {
    padding: 12px 24px;
    border-top: 1px solid #e0e0e0;
    background: white;
    flex-shrink: 0;

    .footer-info {
      .info-text {
        color: #666;
        font-size: 0.85rem;
        font-weight: 500;
      }
    }
  }
}
</style>